import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { ArrowUpRight, BookOpenText, Braces, FileText, Languages, PencilLine, Quote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { shortCite, toCslJson } from "@/lib/citations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import { LibraryStatusMenu } from "@/components/library/LibraryStatusMenu";
import {
  deriveLibraryTitle,
  getLibraryItemStatusLabel,
  getLibraryProviderLabel,
  type LibraryItem,
  type LibraryItemStatus,
} from "@/lib/library";
import { useLibraryCoverUrl } from "@/hooks/useLibraryCoverUrl";
import { supabase } from "@/integrations/supabase/client";
import { safeHref } from "@/lib/safeHref";

type Category = { id: string; name: string; color: string };
type LinkedExcerpt = { id: string; body_text: string; created_at: string; notes: string | null };

type LibraryItemDetailProps = {
  item: (LibraryItem & { categories?: Category[]; excerptCount?: number }) | null;
  linkedExcerpts: LinkedExcerpt[];
  onEdit: () => void;
  onCreateExcerpt: () => void;
  onViewExcerpts: () => void;
  onDelete: () => void;
  onChangeStatus?: (itemId: string, status: LibraryItemStatus) => void | Promise<void>;
  returnTo?: string;
};

type MetadataRow = {
  label: string;
  value: string | null;
  href?: string | null;
};

const formatAccessedDate = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString();
};

const metadataRows = (item: LibraryItem): MetadataRow[] => [
  { label: "Status", value: getLibraryItemStatusLabel(item.status) },
  { label: "Subtitle", value: item.subtitle },
  { label: "Author", value: item.author_text },
  { label: "Publisher", value: item.publisher },
  { label: "Site", value: item.container_title },
  { label: "Published", value: item.published_date ?? (item.published_year ? String(item.published_year) : null) },
  { label: "Accessed", value: formatAccessedDate(item.accessed_date) },
  { label: "Language", value: item.language_code },
  { label: "Pages", value: item.page_count != null ? String(item.page_count) : null },
  { label: "Edition", value: item.edition },
  { label: "Volume", value: item.volume },
  { label: "Series", value: item.series },
  { label: "ISBN-13", value: item.isbn_13 },
  { label: "ISBN-10", value: item.isbn_10 },
  { label: "DOI", value: item.doi, href: item.doi ? `https://doi.org/${item.doi}` : null },
  { label: "URL", value: item.url, href: safeHref(item.url) },
  { label: "Provider", value: getLibraryProviderLabel(item.source_provider) },
];

const excerptPreview = (value: string) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > 160 ? `${normalized.slice(0, 157)}…` : normalized;
};

export const LibraryItemDetail = ({ item, linkedExcerpts, onEdit, onCreateExcerpt, onViewExcerpts, onDelete, onChangeStatus, returnTo }: LibraryItemDetailProps) => {
  // Hooks must run unconditionally — keep the early `if (!item)` guard *after*
  // every hook call. Pass nullable cover url; hook handles undefined safely.
  const coverSrc = useLibraryCoverUrl(item?.cover_url ?? null);
  const [coverFailed, setCoverFailed] = useState(false);
  useEffect(() => { setCoverFailed(false); }, [coverSrc]);

  // Lazy-load saved article body for website-type items (e.g. items promoted
  // from My Feed Reader). Stored encrypted; decrypted via SECURITY DEFINER RPC.
  const [bodyHtml, setBodyHtml] = useState<string | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyOpen, setBodyOpen] = useState(false);
  const canHaveBody = item?.source_type === "website";
  useEffect(() => {
    if (!item || !canHaveBody || !bodyOpen || bodyHtml !== null) return;
    let cancelled = false;
    setBodyLoading(true);
    void (supabase as unknown as { rpc: (n: string, a: Record<string, unknown>) => Promise<{ data: string | null }> })
      .rpc("get_decrypted_library_item_body", { _item_id: item.id })
      .then(({ data }) => {
        if (cancelled) return;
        setBodyHtml(typeof data === "string" ? data : "");
        setBodyLoading(false);
      });
    return () => { cancelled = true; };
  }, [item, canHaveBody, bodyOpen, bodyHtml]);

  const sanitizedBody = useMemo(() => {
    if (!bodyHtml) return "";
    return DOMPurify.sanitize(bodyHtml, { USE_PROFILES: { html: true }, ADD_ATTR: ["target", "rel"] });
  }, [bodyHtml]);

  if (!item) return null;

  const title = deriveLibraryTitle(item);


  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardContent className="flex flex-col gap-5 py-5">
          <div className="flex gap-4">
            {coverSrc && !coverFailed ? (
              <div className="h-36 w-24 shrink-0 overflow-hidden rounded-sm border border-border bg-muted">
                <img
                  src={coverSrc}
                  alt={title}
                  className="h-full w-full object-contain"
                  loading="lazy"
                  onError={() => setCoverFailed(true)}
                />
              </div>
            ) : null}
            <div className="min-w-0 flex-1 space-y-3">
              <div className="space-y-1">
                <h2 className="font-serif text-2xl leading-tight text-foreground">{title}</h2>
                <p className="text-sm text-muted-foreground">{item.author_text || "Unknown author"}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {onChangeStatus ? (
                  <LibraryStatusMenu
                    status={item.status}
                    onChange={(next) => onChangeStatus(item.id, next)}
                    variant="badge"
                  />
                ) : (
                  <Badge variant="secondary">{getLibraryItemStatusLabel(item.status)}</Badge>
                )}
                {item.language_code ? <Badge variant="secondary"><Languages className="h-3 w-3" />{item.language_code}</Badge> : null}
                <Badge variant="outline"><BookOpenText className="h-3 w-3" />{item.excerptCount ?? 0} excerpt{item.excerptCount === 1 ? "" : "s"}</Badge>
                <Badge variant="outline">{getLibraryProviderLabel(item.source_provider)}</Badge>
              </div>
              {item.categories?.length ? (
                <div className="flex flex-wrap gap-2">
                  {item.categories.map((category) => <LibraryCategoryBadge key={category.id} category={category} />)}
                </div>
              ) : null}
              {item.source_type === "book" && item.is_owned ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Badge variant="outline">Owned</Badge>
                  {item.book_binding ? (
                    <Badge variant="outline">{item.book_binding === "hardback" ? "Hardback" : "Paperback"}</Badge>
                  ) : null}
                  {item.is_first_edition ? <Badge variant="outline">First edition</Badge> : null}
                  {item.is_numbered ? (
                    <Badge variant="outline">
                      {item.numbered_index != null && item.numbered_total != null
                        ? `Numbered ${item.numbered_index}/${item.numbered_total}`
                        : "Numbered"}
                    </Badge>
                  ) : null}
                  {item.is_signed ? <Badge variant="outline">Signed</Badge> : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            <Button onClick={onCreateExcerpt}><Quote className="h-4 w-4" /> New excerpt</Button>
            <Button variant="outline" onClick={onViewExcerpts}><ArrowUpRight className="h-4 w-4" /> View all excerpts</Button>
            <Button variant="outline" onClick={onEdit}><PencilLine className="h-4 w-4" /> Edit record</Button>
            <Button variant="outline" onClick={onDelete}><Trash2 className="h-4 w-4" /> Remove</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-base">Record details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              {metadataRows(item).filter((row) => row.value).map((row) => (
                <div key={row.label} className="space-y-1 rounded-md border border-border bg-muted/20 p-3">
                  <p className="text-xs uppercase text-muted-foreground">{row.label}</p>
                  {row.href ? (
                    <a
                      href={row.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary underline-offset-4 hover:underline break-all"
                    >
                      {row.value}
                    </a>
                  ) : (
                    <p className="text-sm text-foreground break-words">{row.value}</p>
                  )}
                </div>
              ))}
            </div>
            {item.description ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Synopsis{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    (Text may be truncated at 1,000 characters.)
                  </span>
                </p>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{item.description}</p>
              </div>
            ) : null}
            {item.notes ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Notes</p>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{item.notes}</p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle className="text-base">Citation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-md border border-border bg-muted/20 p-3">
                <p className="text-xs uppercase text-muted-foreground">Short cite</p>
                <p className="font-serif text-base text-foreground">{shortCite(item)}</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(shortCite(item));
                      toast.success("Short cite copied");
                    } catch {
                      toast.error("Couldn't copy to clipboard");
                    }
                  }}
                >
                  <Quote className="h-4 w-4" /> Copy short cite
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(JSON.stringify(toCslJson(item), null, 2));
                      toast.success("CSL-JSON copied");
                    } catch {
                      toast.error("Couldn't copy to clipboard");
                    }
                  }}
                >
                  <Braces className="h-4 w-4" /> Copy CSL-JSON
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                CSL-JSON works with Zotero, Pandoc, and most reference managers.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle className="text-base">Linked excerpts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {linkedExcerpts.length ? (
                linkedExcerpts.map((excerpt) => (
                  <Link key={excerpt.id} to={`/excerpts/${excerpt.id}?libraryItem=${item.id}&returnTo=${encodeURIComponent(returnTo || `my-library:${item.id}`)}`} className="block rounded-md border border-border bg-muted/20 p-3 transition-colors hover:bg-muted/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-2">
                        <p className="text-sm text-foreground">{excerptPreview(excerpt.body_text)}</p>
                        <p className="text-xs text-muted-foreground">Saved {new Date(excerpt.created_at).toLocaleDateString()}</p>
                      </div>
                      <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                  </Link>
                ))
              ) : (
                <div className="rounded-md border border-dashed border-border bg-muted/10 p-4 text-sm text-muted-foreground">
                  No excerpts linked yet. Create the first excerpt directly from this source record.
                </div>
              )}
              <div className="flex flex-col gap-2 pt-1">
                <Button variant="outline" className="w-full whitespace-normal text-left h-auto py-2" onClick={onViewExcerpts}>
                  <ArrowUpRight className="h-4 w-4 shrink-0" /> View all excerpts for this source
                </Button>
                <Button variant="outline" className="w-full whitespace-normal text-left h-auto py-2" onClick={onCreateExcerpt}>
                  <Quote className="h-4 w-4 shrink-0" /> Create excerpt from this source
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {canHaveBody && (
        <Card className="shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Saved article
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setBodyOpen((v) => !v)}>
              {bodyOpen ? "Hide" : "Show"} article body
            </Button>
          </CardHeader>
          {bodyOpen && (
            <CardContent>
              {bodyLoading ? (
                <p className="text-sm text-muted-foreground">Loading saved article…</p>
              ) : sanitizedBody ? (
                <div className="feed-article" dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No saved article body for this source. Open the original URL to read it.
                </p>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};